import { Box, Button, IconButton, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { HrBotContext } from 'components/@home/drawers/HrBotDrawer/hrBotContext';
import ItemCheckBox from 'components/@home/drawers/HrBotDrawer/ItemCheckBox';
import {
  getBoxJoinAndPositionFromSpanElements,
  getXPathValue,
  mergeExtractedPartsPreserveNonEmpty,
  refreshBoxFoundPartsFromPdf,
} from 'components/@home/drawers/HrBotDrawer/pdf-utils';
import {
  collectPresentationSpansSameRow,
  selectPresentationSpansInCanvasRect,
  selectionRectCanvasClientFromInternalDrag,
} from 'components/@home/drawers/HrBotDrawer/selection-utils';
import XmlPreview from 'components/@home/drawers/HrBotDrawer/XmlPreview';
import PdfPreview from 'components/@home/modals/PdfPreview';
import Autosuggest from 'components/controls/Autosuggest';
import CopyField from 'components/controls/CopyField';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import startCase from 'lodash/startCase';
import throttle from 'lodash/throttle';
import FormatTextdirectionLToRIcon from 'mdi-react/FormatTextdirectionLToRIcon';
import FormatTextdirectionRToLIcon from 'mdi-react/FormatTextdirectionRToLIcon';
import React, {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import documentFormats from 'store/app/entities/documentFormats/action';
import authUser from 'store/selectors/authUser';
import hasDivisionsSelector from 'store/selectors/hasDivisionsSelector';

const getSelectedNodes = () => {
  if (!document.getSelection) {
    return [];
  }
  const sel = document.getSelection();
  if (sel.rangeCount < 1) {
    return [];
  }
  try {
    const range = sel.getRangeAt(0).cloneRange();
    if (range.collapsed) {
      return [];
    }
    if (range.startContainer.nodeType === 3) {
      range.setStartBefore(range.startContainer.parentElement);
    }
    if (range.endContainer.nodeType === 3) {
      range.setEndAfter(range.endContainer.parentElement);
    }
    return Array.from(range.cloneContents().querySelectorAll('span'));
  } catch {
    return [];
  }
};

const HrBotConfig = () => {
  const theme = useTheme();
  const hasDivisions = useSelector(hasDivisionsSelector);
  const user = useSelector(authUser);
  const dispatch = useDispatch();
  const { currentFile, formats, formatId, navigateBack } = useContext(HrBotContext);
  const { pageNum: currentPage } = currentFile;
  const canvas = useRef();
  const format = formats[formatId];
  const [selectedItem, setSelectedItem] = useState(null);
  const [foundItems, setFoundItems] = useState(currentFile || {});
  const [items, setItems] = useState(
    format?.items.reduce((acc, curr) => ({ ...acc, [curr.name]: curr }), {}) || {},
  );
  const item = items[selectedItem];
  const [handlersStatus, setHandlersStatus] = useState('setting');
  const [scale, setScale] = useState(null);

  const getPageUnits = useCallback(n => +(n * scale).toFixed(3), [scale]);
  const getCanvasUnits = useCallback(n => n / scale, [scale]);

  const [reverse, setReverse] = useState(false);
  const [status, setStatus] = useState(null);

  const [selecting, setSelecting] = useState(false);

  const setItem = it => {
    const newItem = { ...it };
    delete newItem.selected;
    if (it.error) {
      setFoundItems(fi => {
        const newItems = { ...fi };
        delete newItems[it.name];
        return newItems;
      });
    }
    setItems(its => ({ ...its, [newItem.name]: newItem }));
  };

  const itemsBoxSig = useMemo(
    () =>
      JSON.stringify(
        Object.values(items).filter(
          i =>
            i.selector === 'box' &&
            i.coords &&
            typeof i.coords === 'object' &&
            i.coords.left != null,
        ),
      ),
    [items],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!['single-pdf', 'multi-pdf'].includes(format?.type)) return;
      if (!currentFile?.file) return;
      if (!itemsBoxSig || itemsBoxSig === '[]') return;

      await refreshBoxFoundPartsFromPdf(
        currentFile.file,
        { ...format, items: Object.values(items) },
        currentPage,
        parts => {
          if (!cancelled) {
            setFoundItems(prev => mergeExtractedPartsPreserveNonEmpty(prev, parts));
          }
        },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [itemsBoxSig, currentFile?.file, currentPage, format, items]);

  useEffect(() => {
    if (canvas.current) {
      const ctx = canvas.current.getContext('2d');
      ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);
      if (item?.selector === 'box' && item?.coords?.left != null) {
        ctx.strokeStyle = theme.palette.tertiary.dark;
        const dpr = window.devicePixelRatio || 1;
        ctx.lineWidth = 1.5 * dpr;
        ctx.strokeRect(
          getCanvasUnits(item.coords.left),
          getCanvasUnits(item.coords.top),
          getCanvasUnits(item.coords.right - item.coords.left),
          getCanvasUnits(item.coords.bottom - item.coords.top),
        );
      }
    }
  }, [item, getCanvasUnits, theme.palette.tertiary.dark]);

  const toggleTextOnly = (event, value) => {
    if (!canvas.current) {
      return;
    }
    const pageEl = canvas.current.nextSibling;
    const pdfCanvas =
      pageEl?.querySelector?.('.react-pdf__Page__canvas') || pageEl?.querySelector?.('canvas');
    const textLayer = pageEl?.querySelector?.('[data-pdf-text-layer]');
    const spans = textLayer ? textLayer.querySelectorAll('span[role="presentation"]') : [];
    if (value && (!textLayer || spans.length === 0)) {
      return;
    }
    if (pdfCanvas) {
      pdfCanvas.style.visibility = value ? 'hidden' : 'visible';
    }
    spans.forEach(el => {
      const c = value ? '#000' : 'transparent';
      el.style.setProperty('color', c, 'important');
      el.style.setProperty('-webkit-text-fill-color', c, 'important');
    });
  };

  const start = useRef([0, 0]);
  const end = useRef([0, 0]);
  const isDown = useRef(false);

  const getMouseCoords = e => {
    const bounds = canvas.current.getBoundingClientRect();
    return [
      (e.offsetX / bounds.width) * canvas.current.width,
      (e.offsetY / bounds.height) * canvas.current.height,
    ];
  };

  const handleMouseDown = useCallback(
    e => {
      if (!selecting) {
        isDown.current = false;
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      // get references to the canvas and context
      const ctx = canvas.current.getContext('2d');
      ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);
      const selection = window.getSelection();
      selection.removeAllRanges();
      setItem({ ...item, coords: null });

      // save the starting x/y of the rectangle
      start.current = getMouseCoords(e);

      // set a flag indicating the drag has begun
      isDown.current = true;
    },
    [item, selecting],
  );

  const handleMouseUp = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      toggleTextOnly(e, false);
      setSelecting(false);

      // the drag is over, clear the dragging flag
      isDown.current = false;
      const pageEl = canvas.current.nextSibling;
      const textLayer = pageEl?.querySelector?.('[data-pdf-text-layer]') ?? null;
      if (!canvas.current || !textLayer) {
        return;
      }
      const rect = selectionRectCanvasClientFromInternalDrag(
        canvas.current,
        start.current[0],
        start.current[1],
        end.current[0],
        end.current[1],
      );
      const selResult = selectPresentationSpansInCanvasRect(canvas.current, textLayer, rect);
      if (selResult.multiline) {
        setItem({ ...item, error: 'ERROR: You selected multiple lines' });
        return;
      }
      const { selectedText, spansForPos } = selResult;
      if (!selectedText) {
        setItem({ ...item, error: 'You should select whole words' });
        return;
      }
      if (!selectedText?.match(/^\w([\w-/]*\w)?$/)) {
        setItem({
          ...item,
          error:
            'ERROR: The text must start and end with a letter or number and only include letters, numbers, hyphens, or slashes.',
        });
        return;
      }
      const { join, pos } = getBoxJoinAndPositionFromSpanElements(
        spansForPos,
        selectedText,
        reverse,
      );
      if (pos === null) {
        setItem({ ...item, error: 'ERROR: You should select the whole word' });
        return;
      }
      if (item?.selector === 'box') {
        const newItem = {
          ...item,
          position: pos,
          coords: {
            left: Math.min(getPageUnits(start.current[0]), getPageUnits(end.current[0])),
            top: Math.min(getPageUnits(start.current[1]), getPageUnits(end.current[1])),
            right: Math.max(getPageUnits(start.current[0]), getPageUnits(end.current[0])),
            bottom: Math.max(getPageUnits(start.current[1]), getPageUnits(end.current[1])),
          },
        };
        if (join) {
          newItem.join = join;
        } else {
          delete newItem.join;
        }
        delete newItem.error;
        setItem(newItem);
        setFoundItems(fi => ({
          ...fi,
          [item.name]: selectedText,
        }));
      }
    },
    [item, reverse, getPageUnits],
  );

  const handleMouseOut = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    // the drag is over, clear the dragging flag
    isDown.current = false;
  }, []);

  const handleMouseMove = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      // if we're not dragging, just return
      if (!selecting || !isDown.current) {
        return;
      }

      // get the current mouse position
      const [mouseX, mouseY] = getMouseCoords(e);

      if (!mouseX && !mouseY) {
        // Weird error in Firefox where mouse coords are zero
        return;
      }

      // Put your mousemove stuff here

      // get references to the canvas and context
      const ctx = canvas.current.getContext('2d');

      // style the context
      const dpr = window.devicePixelRatio || 1;
      ctx.strokeStyle = theme.palette.primary.main;
      ctx.lineWidth = 1.5 * dpr;
      // clear the canvas
      ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);

      // calculate the rectangle width/height based
      // on starting vs current mouse position
      const width = mouseX - start.current[0];
      const height = mouseY - start.current[1];
      end.current = [mouseX, mouseY];

      // draw a new rect from the start position
      // to the current mouse position
      ctx.strokeRect(start.current[0], start.current[1], width, height);
    },
    [selecting, theme.palette.primary.main],
  );

  const handleMouseMoveRef = useRef(handleMouseMove);
  handleMouseMoveRef.current = handleMouseMove;
  const handleMouseMoveThrottled = useMemo(
    () => throttle(e => handleMouseMoveRef.current(e), 100),
    [],
  );

  useEffect(() => {
    const currentCanvas = canvas.current;

    if (item?.selector !== 'box') {
      if (handlersStatus !== 'setting') {
        setHandlersStatus('setting');
      }
      return () => {};
    }

    if (!currentCanvas) {
      if (handlersStatus !== 'not set') {
        setHandlersStatus('not set');
        setScale(null);
      }
      return () => {};
    }
    if (handlersStatus === 'set') {
      currentCanvas.addEventListener('mousedown', handleMouseDown);
      currentCanvas.addEventListener('mousemove', handleMouseMoveThrottled);
      currentCanvas.addEventListener('mouseup', handleMouseUp);
      currentCanvas.addEventListener('mouseout', handleMouseOut);
      return () => {
        handleMouseMoveThrottled.cancel?.();
        currentCanvas.removeEventListener('mousedown', handleMouseDown);
        currentCanvas.removeEventListener('mousemove', handleMouseMoveThrottled);
        currentCanvas.removeEventListener('mouseup', handleMouseUp);
        currentCanvas.removeEventListener('mouseout', handleMouseOut);
        setHandlersStatus('setting');
      };
    }
    if (!currentCanvas && handlersStatus === 'setting') {
      setHandlersStatus('not set');
      setTimeout(() => setHandlersStatus('setting'), 500);
      return () => {};
    }

    setHandlersStatus('set');

    return () => {};
  }, [
    scale,
    handlersStatus,
    item?.selector,
    selectedItem,
    reverse,
    selecting,
    handleMouseDown,
    handleMouseMoveThrottled,
    handleMouseUp,
    handleMouseOut,
  ]);

  const handleScaleChange = useCallback(s => {
    if (typeof s === 'number' && s > 0 && Number.isFinite(s)) {
      setScale(s);
    }
  }, []);

  const handleSave = async () => {
    setStatus('saving');
    // Persist first so Redux `format` is up-to-date before StepFiles re-runs parseFiles.
    await dispatch(
      documentFormats.update(format._id, {
        items: Object.values(items),
      }),
    );
    navigateBack();
    setStatus(null);
  };

  const handleXpath = xml => async xpath => {
    if (selecting) {
      const newItem = {
        ...item,
        selector: 'xpath',
        xpath,
      };
      delete newItem.error;
      setItem(newItem);
      const value = await getXPathValue(xml, xpath);
      setFoundItems(fi => ({
        ...fi,
        [newItem.name]: value,
      }));
      setSelecting(false);
    }
  };

  const handleXpathChange = async e => {
    setItem({ ...item, xpath: e.target.value });
  };

  const handleDirection = () => {
    const selectedText = document.getSelection().toString().trim();
    const it = { ...item };
    const nodes = getSelectedNodes();
    const tl = nodes[0]?.closest?.('[data-pdf-text-layer]');
    const spansForPos = tl && nodes.length ? collectPresentationSpansSameRow(tl, nodes, tl) : nodes;
    const { pos } = getBoxJoinAndPositionFromSpanElements(spansForPos, selectedText, !reverse);
    if (pos != null) {
      it.position = pos;
    } else {
      delete it.position;
    }
    setItem(it);
    setReverse(!reverse);
  };

  const separatorRegex = new RegExp(`[${item?.separators}]+`);
  const fileNameParts =
    (item?.separators
      ? currentFile?.file?.name.split(separatorRegex)
      : [currentFile?.file?.name]) || [];

  const position = isNil(item?.position) || item.position === '' ? NaN : Number(item.position);
  const hasPosition = Number(position) >= 0;
  const itemNames = [format.sendBy || 'code', 'dni', 'year', 'month', 'day'];

  if (hasDivisions) {
    itemNames.unshift('division');
  }

  if (format.type === 'xml') {
    itemNames.unshift('filename');
  }

  const disabled = itemNames.some(
    name =>
      (items[name]?.selector === 'fileName' && (!hasPosition || !items[name]?.separators)) ||
      (items[name]?.selector === 'xpath' && !items[name]?.xpath) ||
      (items[name]?.selector === 'coords' && !items[name]?.coords) ||
      (items[name]?.selector === 'box' &&
        (!foundItems[name] || !items[name]?.coords || items[name]?.error)),
  );

  const handleItemChange = it => {
    setItems(oldItems => {
      const newItems = { ...oldItems };
      if (it.selected) {
        newItems[it.name] = { ...it };
        delete newItems[it.name].selected;
        delete newItems[it.name].join;
      } else {
        delete foundItems[it.name];
        delete newItems[it.name];
      }
      return newItems;
    });
  };

  return (
    <>
      <Box
        sx={{
          mt: 1,
          mb: 1,
          flexGrow: 1,
          display: 'flex',
          maxHeight: '100%',
          overflow: 'auto',
          alignItems: 'flex-start',
          flexDirection: 'column',
          cursor: selecting ? (format.type === 'xml' ? 'not-allowed' : 'crosshair') : 'default',
        }}
      >
        <Box
          onClick={e => {
            toggleTextOnly(e, false);
            setSelecting(false);
          }}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            background: 'black',
            opacity: selecting ? 0.7 : 0,
            zIndex: 100,
            transition: 'opacity 400ms ease-in-out',
            pointerEvents: selecting ? 'all' : 'none',
          }}
        />
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          {format && (
            <div key={formatId}>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& tr': {
                    verticalAlign: 'top',
                  },
                }}
              >
                <tbody>
                  <tr>
                    {itemNames.map(it => {
                      return (
                        <Box component="td" key={it}>
                          <ItemCheckBox
                            value={items[it]}
                            found={foundItems[it]}
                            name={it}
                            onClick={() => {
                              setSelectedItem(it);
                            }}
                            onChange={handleItemChange}
                            onSelectionDrag={e => {
                              setSelectedItem(it);
                              if (['single-pdf', 'multi-pdf'].includes(format.type)) {
                                toggleTextOnly(e, true);
                              }
                              setSelecting(true);
                            }}
                            selected={selectedItem === it}
                            type={format.type}
                          />
                        </Box>
                      );
                    })}
                  </tr>
                </tbody>
              </Box>
              <div>
                {user.isSuperAdmin && selectedItem && (
                  <div>
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        mt: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Autosuggest
                          fullWidth
                          value={item?.selector}
                          onChange={v => {
                            setItem({ ...item, selector: v.value });
                          }}
                          suggestions={['box', 'xpath', 'fileName', 'coords', 'user'].map(
                            value => ({
                              value,
                              label: startCase(value),
                            }),
                          )}
                        />
                      </Box>
                      {item?.selector === 'box' && (
                        <IconButton onClick={handleDirection} sx={{ pt: 0, pb: 0 }} size="large">
                          {reverse ? (
                            <FormatTextdirectionRToLIcon />
                          ) : (
                            <FormatTextdirectionLToRIcon />
                          )}
                        </IconButton>
                      )}
                    </Box>
                    {item?.selector === 'user' && (
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          mt: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Autosuggest
                            isClearable
                            fullWidth
                            value={item?.defaultValue}
                            clearValue={() => {
                              setItem({ ...item, defaultValue: '' });
                            }}
                            onChange={v => {
                              setItem({ ...item, defaultValue: v?.value });
                            }}
                            suggestions={['currentMonth', 'previousMonth'].map(value => ({
                              value,
                              label: startCase(value),
                            }))}
                          />
                        </Box>
                      </Box>
                    )}
                    {item?.selector === 'box' && (
                      <Box sx={{ mt: '10px' }}>
                        <CopyField
                          value={
                            item &&
                            JSON.stringify(pick(item, ['error', 'coords', 'position', 'join']))
                          }
                        />
                      </Box>
                    )}
                    {item?.selector === 'xpath' && (
                      <div>
                        <TextField
                          fullWidth
                          value={item.xpath}
                          onChange={handleXpathChange}
                          sx={{ mt: '10px' }}
                        />
                      </div>
                    )}
                    {item?.selector === 'coords' && (
                      <div>
                        <TextField
                          fullWidth
                          value={item.coords}
                          onChange={e => {
                            setItem({ ...item, coords: e.target.value });
                          }}
                          sx={{ mt: '10px' }}
                        />
                      </div>
                    )}
                    {item?.selector === 'fileName' && (
                      <div>
                        <TextField
                          fullWidth
                          placeholder="Separators"
                          value={item.separators}
                          onChange={e => {
                            setItem({ ...item, separators: e.target.value });
                          }}
                          sx={{ mt: '10px' }}
                        />
                        <TextField
                          fullWidth
                          placeholder="Position"
                          value={item.position}
                          onChange={e => {
                            setItem({ ...item, position: e.target.value });
                          }}
                          type="number"
                          sx={{ mt: '10px' }}
                        />
                        <Box
                          sx={muiTheme => ({
                            border: `solid 1px ${muiTheme.palette.secondary.light}`,
                            borderRadius: '6px',
                            p: '10px',
                            height: '1.1876em',
                            fontSize: '0.8125rem',
                            mt: '10px',
                            boxSizing: 'content-box',
                          })}
                        >
                          {fileNameParts.map((part, idx) => (
                            <Fragment key={idx}>
                              {idx > 0 && (
                                <Typography
                                  component="span"
                                  sx={{
                                    display: 'inline',
                                  }}
                                >
                                  {' '}
                                </Typography>
                              )}
                              <Typography
                                component="span"
                                sx={[
                                  {
                                    display: 'inline',
                                  },
                                  hasPosition && idx === +item.position
                                    ? muiTheme => ({
                                        backgroundColor: muiTheme.palette.primary.extraLight,
                                      })
                                    : undefined,
                                ]}
                              >
                                {part}
                              </Typography>
                            </Fragment>
                          ))}
                        </Box>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </Box>
        <Box
          sx={{
            mt: 1,
            borderRadius: '6px',
            overflow: 'auto',
            width: '100%',
            zIndex: selecting ? 200 : 'auto',
          }}
        >
          {['single-pdf', 'multi-pdf'].includes(format.type) && (
            <PdfPreview
              onScaleChange={handleScaleChange}
              renderTextLayer={false}
              canvasRef={canvas}
              src={currentFile.file}
              showSignature={false}
              pageNum={currentPage}
              fullWidth
            />
          )}
          {format.type === 'xml' && (
            <XmlPreview xml={currentFile.xml} onClick={selecting && handleXpath(currentFile.xml)} />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'baseline',
          mb: 2,
        }}
      >
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={disabled || status === 'saving'}
          sx={muiTheme => ({
            mt: '10px',
            mr: '10px',
            background: `linear-gradient(left, darken(${muiTheme.palette.primary.main},20%), darken(${muiTheme.palette.primary.main},20%) 50%, ${muiTheme.palette.primary.main} 50%, ${muiTheme.palette.primary.main})`,
            backgroundSize: '200% 100%',
            backgroundPosition: '100% 0',
            transition: 'background-position .3s ease-out',
          })}
        >
          {status === 'saving' ? 'Saving...' : 'Save'}
        </Button>
        <Button
          onClick={navigateBack}
          color="secondary"
          variant="outlined"
          sx={muiTheme => ({
            mt: '10px',
            mr: '10px',
            background: `linear-gradient(left, darken(${muiTheme.palette.primary.main},20%), darken(${muiTheme.palette.primary.main},20%) 50%, ${muiTheme.palette.primary.main} 50%, ${muiTheme.palette.primary.main})`,
            backgroundSize: '200% 100%',
            backgroundPosition: '100% 0',
            transition: 'background-position .3s ease-out',
          })}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};

export default memo(HrBotConfig);
